import { z } from 'zod';

// Define the available event types as a string literal union
export type TrackingEventType =
  | 'Example'
  | 'ExampleBlacklisted'
  | 'ExampleWithPayload'
  | 'Error'
  | 'BeforeSubmitHook'
  | 'BuyAtProviderError'
  | 'BuyAtProviderFailed'
  | 'BuyAtProviderSuccess'
  | 'BuyAtProviderValidation'
  | 'BuyAtProviderValidationError'
  | 'BuyAtProviderValidationFailed'
  | 'BuyAtProviderValidationSuccess'
  | 'BuyButtonClick'
  | 'BuyBackendSubmitFailed'
  | 'BuyPaymentRedirect'
  | 'BuyShowSummary'
  | 'BuySubmit'
  | 'ExpressCheckoutButtonClick'
  | 'PageView'
  | 'OrderFormLoaded'
  | 'OrderFormStart'
  | 'SignupFormStepChange'
  | 'SignupFormButtonClick'
  | 'SignupFormFieldFocus'
  | 'OrderFormFieldBlur'
  | 'OrderFormFieldFocus'
  | 'SignupFormFieldBlur'
  | 'FindMyOrder_Search_Start'
  | 'FindMyOrder_Search_Success'
  | 'FindMyOrder_Search_Fail'
  | 'FindMyOrder_ViewInvoice'
  | 'FindMyOrder_ViewProduct'
  | 'FindMyOrder_CancelRebilling'
  | 'FindMyOrder_ChangePayment'
  | 'FindMyOrder_RequestRefund';

// Re-export the TrackingEvent type and EventPriority enum
export { TrackingEvent, EventPriority } from './tracking-event';

// Define Zod schemas for each event type's payload
export const ExamplePayloadSchema = z.object({
  pageId: z.string(),
  pageTitle: z.string(),
  referrer: z.string().optional(),
  url: z.string(),
});

export const ErrorPayloadSchema = z.instanceof(Error);

// Schema for events with no payload
export const EmptyPayloadSchema = z.undefined();

export const PageViewPayloadSchema = z.object({
  path: z.string(),
  title: z.string(),
});

export const SignupFormStepChangePayloadSchema = z.undefined();
export const SignupFormFieldFocusPayloadSchema = z.object({
  field: z.string(),
});
export const SignupFormFieldBlurPayloadSchema = z.object({
  field: z.string(),
});
export const SignupFormButtonClickPayloadSchema = z.object({
  button: z.string(),
});

export const OrderFormPayloadBaseSchema = z.object({
  hasCookieConsent: z.boolean(),
  iframeEmbedded: z.boolean().optional(),
  mainProductId: z.number().optional(),
  mainProductTypeId: z.number().optional(),
  defaultPrice: z.number().optional(),
  siteownerId: z.number().optional(),
  pageType: z.enum(['orderform', 'renew']).optional(),
  vendor: z.string().optional(),
  affiliate: z.string().optional(),
  orderformId: z.number().optional(),
  orderCheckoutType: z.enum(['express', 'default']).optional(),
  orderType: z.enum(['initial', 'upsell']).optional(),
  orderBuyerCountry: z.string().optional(),
  orderCurrency: z.string().optional(),
  orderValue: z.number().optional(),
  paymentMethod: z.string().nullable().optional(),
  paymentProviderId: z.string().nullable().optional(),
  orderBillingType: z.string().optional(),
  referrer: z.string().optional(),
  os: z.string().optional(),
  browser: z.string().optional(),
  browserVersion: z.string().optional(),
  app: z.string().optional(),
  deviceType: z.string().optional(),
  screenWidth: z.number().optional(),
  screenHeight: z.number().optional(),
});

export type OrderFormPayloadBaseType = z.infer<typeof OrderFormPayloadBaseSchema>;

export const OrderFormStartPayloadSchema = OrderFormPayloadBaseSchema.extend({
  firstInput: z.number().optional(),
  largestContentfulPaint: z.number().optional(),
  firstContentfulPaint: z.number().optional(),
});

export const BuyButtonClickPayloadSchema = OrderFormPayloadBaseSchema.extend({
  orderFormSummaryType: z.enum(['none', 'above-button', 'dialog']),
});

export const OrderFormFieldFocusPayloadSchema = OrderFormPayloadBaseSchema.extend({
  fieldName: z.string().optional(),
});

export const OrderFormFieldBlurPayloadSchema = OrderFormPayloadBaseSchema.extend({
  fieldName: z.string().optional(),
  hadError: z.boolean().optional(),
  fieldValueProvided: z.boolean().optional(),
});

export type OrderFormFieldBlurPayload = z.infer<typeof OrderFormFieldBlurPayloadSchema>;
export type OrderFormFieldFocusPayload = z.infer<typeof OrderFormFieldFocusPayloadSchema>;

// FindMyOrder event payload schemas
export const FindMyOrderSearchStartPayloadSchema = z.object({
  searchType: z.string(),
  usedField: z.string(),
  locale: z.string(),
  is_cancellation: z.boolean().optional(),
});

export const FindMyOrderSearchSuccessPayloadSchema = z.object({
  searchType: z.string(),
  locale: z.string(),
  is_cancellation: z.boolean().optional(),
  purchase: z.object({
    // order_id: z.string(), // GDPR: Temporarily removed for privacy compliance
    reseller: z.string().optional(),
    product_type: z.string().optional(),
    billing_type: z.string().optional(),
  }),
});

export const FindMyOrderSearchFailPayloadSchema = z.object({
  searchType: z.string(),
  locale: z.string(),
  is_cancellation: z.boolean().optional(),
});

export const FindMyOrderActionPayloadSchema = z.object({
  // order_id: z.string(), // GDPR: Temporarily removed for privacy compliance
  locale: z.string(),
  reseller: z.string().optional(),
  product_type: z.string().optional(),
  billing_type: z.string().optional(),
});

// Used for type inference of payloads based on their event type.
// We explicitly want to avoid interface merging because we need an immutable mapping of event types to payloads.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type EventPayloadMap = {
  Example: undefined;
  ExampleBlacklisted: undefined;
  ExampleWithPayload: z.infer<typeof ExamplePayloadSchema>;
  Error: z.infer<typeof ErrorPayloadSchema>;
  BuyPaymentRedirect: undefined;
  BuyAtProviderFailed: undefined;
  BuyAtProviderSuccess: undefined;
  BuyAtProviderError: undefined;
  BuyAtProviderValidation: undefined;
  BuyAtProviderValidationFailed: undefined;
  BuyAtProviderValidationSuccess: undefined;
  BuyAtProviderValidationError: undefined;
  BeforeSubmitHook: undefined;
  BuySubmit: undefined;
  BuyButtonClick: z.infer<typeof BuyButtonClickPayloadSchema>;
  ExpressCheckoutButtonClick: undefined;
  BuyShowSummary: z.infer<typeof BuyButtonClickPayloadSchema>;
  PageView: z.infer<typeof PageViewPayloadSchema>;
  OrderFormLoaded: z.infer<typeof OrderFormPayloadBaseSchema>;
  OrderFormStart: z.infer<typeof OrderFormStartPayloadSchema>;
  BuyBackendSubmitFailed: z.infer<typeof OrderFormPayloadBaseSchema>;
  SignupFormFieldFocus: z.infer<typeof SignupFormFieldFocusPayloadSchema>;
  SignupFormFieldBlur: z.infer<typeof SignupFormFieldBlurPayloadSchema>;
  OrderFormFieldFocus: z.infer<typeof OrderFormFieldFocusPayloadSchema>;
  OrderFormFieldBlur: z.infer<typeof OrderFormFieldBlurPayloadSchema>;
  SignupFormStepChange: z.infer<typeof SignupFormStepChangePayloadSchema>;
  SignupFormButtonClick: z.infer<typeof SignupFormButtonClickPayloadSchema>;
  FindMyOrder_Search_Start: z.infer<typeof FindMyOrderSearchStartPayloadSchema>;
  FindMyOrder_Search_Success: z.infer<typeof FindMyOrderSearchSuccessPayloadSchema>;
  FindMyOrder_Search_Fail: z.infer<typeof FindMyOrderSearchFailPayloadSchema>;
  FindMyOrder_ViewInvoice: z.infer<typeof FindMyOrderActionPayloadSchema>;
  FindMyOrder_ViewProduct: z.infer<typeof FindMyOrderActionPayloadSchema>;
  FindMyOrder_CancelRebilling: z.infer<typeof FindMyOrderActionPayloadSchema>;
  FindMyOrder_ChangePayment: z.infer<typeof FindMyOrderActionPayloadSchema>;
  FindMyOrder_RequestRefund: z.infer<typeof FindMyOrderActionPayloadSchema>;
};

// A mapping from event type to payload schema for validation
export const EventSchemaMap: { [K in TrackingEventType]: z.ZodType<EventPayloadMap[K]> } = {
  Example: EmptyPayloadSchema,
  ExampleBlacklisted: EmptyPayloadSchema,
  ExampleWithPayload: ExamplePayloadSchema,
  Error: ErrorPayloadSchema,
  BuyPaymentRedirect: EmptyPayloadSchema,
  BuyAtProviderFailed: EmptyPayloadSchema,
  BuyAtProviderSuccess: EmptyPayloadSchema,
  BuyAtProviderError: EmptyPayloadSchema,
  BuyAtProviderValidation: EmptyPayloadSchema,
  BuyAtProviderValidationFailed: EmptyPayloadSchema,
  BuyAtProviderValidationSuccess: EmptyPayloadSchema,
  BuyAtProviderValidationError: EmptyPayloadSchema,
  BeforeSubmitHook: EmptyPayloadSchema,
  BuySubmit: EmptyPayloadSchema,
  BuyButtonClick: BuyButtonClickPayloadSchema,
  ExpressCheckoutButtonClick: EmptyPayloadSchema,
  BuyShowSummary: BuyButtonClickPayloadSchema,
  PageView: PageViewPayloadSchema,
  SignupFormStepChange: SignupFormStepChangePayloadSchema,
  SignupFormFieldFocus: SignupFormFieldFocusPayloadSchema,
  SignupFormFieldBlur: SignupFormFieldBlurPayloadSchema,
  OrderFormFieldFocus: OrderFormFieldFocusPayloadSchema,
  OrderFormFieldBlur: OrderFormFieldBlurPayloadSchema,
  SignupFormButtonClick: SignupFormButtonClickPayloadSchema,
  OrderFormLoaded: OrderFormPayloadBaseSchema,
  OrderFormStart: OrderFormStartPayloadSchema,
  BuyBackendSubmitFailed: OrderFormPayloadBaseSchema,
  FindMyOrder_Search_Start: FindMyOrderSearchStartPayloadSchema,
  FindMyOrder_Search_Success: FindMyOrderSearchSuccessPayloadSchema,
  FindMyOrder_Search_Fail: FindMyOrderSearchFailPayloadSchema,
  FindMyOrder_ViewInvoice: FindMyOrderActionPayloadSchema,
  FindMyOrder_ViewProduct: FindMyOrderActionPayloadSchema,
  FindMyOrder_CancelRebilling: FindMyOrderActionPayloadSchema,
  FindMyOrder_ChangePayment: FindMyOrderActionPayloadSchema,
  FindMyOrder_RequestRefund: FindMyOrderActionPayloadSchema,
} as const;
